import { AnimatePresence, motion } from 'motion/react';
import { History, SquarePen } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { formatRelativeTimeShort } from '@/shared/lib/dates';
import { Button } from '@/shared/ui/shadcn/button';

import type { ThreadSummary } from '../../lib/useCopilotThreads';

interface ThreadPopoverProps {
  threads: ThreadSummary[];
  activeThreadId: string;
  isLoading: boolean;
  onSwitchThread: (threadId: string) => void;
  onNewThread: () => void;
}

const CLOSE_DELAY_MS = 180;

export function ThreadPopover({
  threads,
  activeThreadId,
  isLoading,
  onSwitchThread,
  onNewThread,
}: ThreadPopoverProps) {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const scheduleClose = useCallback(() => {
    closeTimerRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, []);

  const cancelClose = useCallback(() => {
    clearTimeout(closeTimerRef.current);
  }, []);

  const handleEnter = useCallback(() => {
    cancelClose();
    setOpen(true);
  }, [cancelClose]);

  const handleLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Conversation history"
        aria-expanded={open}
        aria-haspopup="true"
        onFocus={handleEnter}
        onBlur={handleLeave}
      >
        <History className="h-4 w-4" />
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className={cn(
              'absolute right-0 top-full z-50 mt-1.5 w-72',
              'rounded-xl border border-white/[0.08]',
              'bg-background/70 backdrop-blur-xl backdrop-saturate-150',
              'shadow-lg shadow-black/20',
              'ring-1 ring-black/5'
            )}
            role="menu"
            aria-label="Conversation threads"
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px rounded-t-xl bg-gradient-to-r from-transparent via-white/15 to-transparent" />

            <div className="flex items-center justify-between px-2 pt-1.5">
              <button
                type="button"
                role="menuitem"
                aria-label="New conversation"
                onClick={() => {
                  onNewThread();
                  setOpen(false);
                }}
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-lg',
                  'text-foreground/60 transition-colors duration-150',
                  'hover:bg-white/[0.08] hover:text-foreground/90'
                )}
              >
                <SquarePen className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="flex flex-col gap-0.5 px-1.5 pt-1 pb-1.5">
              {isLoading && (
                <div className="px-2.5 py-2 text-xs text-muted-foreground/70">Loading...</div>
              )}

              {threads.length === 0 && !isLoading && (
                <div className="px-2.5 py-3 text-center text-xs text-muted-foreground/60">
                  No saved conversations yet
                </div>
              )}

              <div className="max-h-64 overflow-y-auto">
                {threads.map((thread) => {
                  const isActive = thread.key === activeThreadId;
                  const relativeDate = formatRelativeTimeShort(thread.createdAt);
                  return (
                    <button
                      key={thread.key}
                      type="button"
                      role="menuitem"
                      aria-current={isActive ? 'true' : undefined}
                      onClick={() => {
                        onSwitchThread(thread.key);
                        setOpen(false);
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-colors duration-150',
                        isActive
                          ? 'bg-primary/15 text-primary font-medium'
                          : 'text-foreground/70 hover:bg-white/[0.06] hover:text-foreground/90'
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate text-[13px]">{thread.title}</span>
                      {relativeDate && (
                        <span
                          className={cn(
                            'shrink-0 text-[11px] tabular-nums',
                            isActive ? 'text-primary/60' : 'text-muted-foreground/50'
                          )}
                        >
                          {relativeDate}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
